import { useCallback, useEffect, useState } from 'react';

/**
 * Install-prompt state. `mode` is one of:
 *
 * - 'ios': iOS browser (all are WebKit, no install API) — show Share-menu steps.
 * - 'android': Android browser — direct to the Play Store listing for the
 *   native app instead of a PWA install.
 * - null: already installed, running standalone, or dismissed within the last 30 days.
 */

const DISMISSED_AT_KEY = 'pwaInstallDismissedAt';
const INSTALLED_KEY = 'pwaInstalled';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=io.laftel.lingo';

function isSnoozed() {
  const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY));
  return dismissedAt > 0 && Date.now() - dismissedAt < SNOOZE_MS;
}

export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigator.standalone === true
  );
}

export function usePwaInstall() {
  const [hidden, setHidden] = useState(
    () => isStandalone() || isSnoozed() || localStorage.getItem(INSTALLED_KEY) === '1',
  );

  useEffect(() => {
    // Suppress Chrome's own PWA install mini-infobar so it doesn't compete
    // with the Play Store banner below.
    const onBeforeInstallPrompt = (e) => e.preventDefault();
    const onAppInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1');
      setHidden(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    setHidden(true);
  }, []);

  const ua = navigator.userAgent;
  // iPadOS reports itself as MacIntel; the touch check tells it apart from macOS.
  const ios =
    /iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const android = /android/i.test(ua);

  let mode = null;
  if (!hidden) {
    if (ios) mode = 'ios';
    else if (android) mode = 'android';
  }

  return { mode, dismiss };
}
